import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Boxes, DollarSign, FileText, Image, MessageSquare, Package, ShoppingBag, Star, Wrench,
} from "lucide-react";
import { adminApi } from "../../lib/api";
import { formatDateTime, money } from "../../lib/format";
import { AdminHeader, Badge, Panel, StatCard } from "../ui";
import { PageLoader } from "../../components/States";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [stripe, setStripe] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/stats").then((r) => setData(r.data)).catch(() => setData(null));
    adminApi.get("/admin/stripe-status").then((r) => setStripe(r.data)).catch(() => {});
  }, []);

  if (!data) return <PageLoader label="Loading dashboard" />;
  const c = data.counts;

  return (
    <div data-testid="admin-dashboard">
      <AdminHeader title="Dashboard" subtitle="Everything migrated from your old site, ready to edit" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Pages" value={c.pages} icon={FileText} />
        <StatCard label="Catalogue items" value={c.items} icon={Boxes} />
        <StatCard label="Shop products" value={c.products} icon={Package} />
        <StatCard label="Media files" value={c.media} icon={Image} />
        <StatCard label="Builds gallery" value={c.builds} icon={Wrench} />
        <StatCard label="Reviews" value={c.reviews} icon={Star} />
        <StatCard label={`Enquiries (${c.new_enquiries} new)`} value={c.enquiries} icon={MessageSquare} />
        <StatCard label={`Orders (${c.paid_orders} paid)`} value={c.orders} icon={ShoppingBag} />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <Panel>
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 gold-text" />
            <h2 className="text-sm font-semibold uppercase tracking-wider">Revenue (paid)</h2>
          </div>
          <p className="mt-3 font-mono text-3xl font-bold gold-text">{money(data.revenue)}</p>
          {stripe && (
            <p className="mt-3 text-xs text-[color:var(--nq-stone)]">
              Stripe:{" "}
              {stripe.configured ? (
                <Badge tone={stripe.mode === "live" ? "green" : "gold"}>{stripe.mode} mode</Badge>
              ) : (
                <Badge tone="red">not configured</Badge>
              )}
            </p>
          )}
          {stripe && !stripe.configured && (
            <p className="mt-2 text-xs text-[color:var(--nq-stone)]">
              Add your Stripe secret key to start taking card payments. Orders are still recorded
              until then.
            </p>
          )}
        </Panel>

        <Panel className="lg:col-span-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider">Latest enquiries</h2>
            <Link to="/admin/enquiries" className="text-xs font-semibold gold-text hover:underline">
              View all
            </Link>
          </div>
          {data.recent_enquiries.length === 0 ? (
            <p className="mt-4 text-sm text-[color:var(--nq-stone)]">No enquiries yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {data.recent_enquiries.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge tone={e.status === "new" ? "gold" : "neutral"}>{e.status}</Badge>
                  <span className="font-medium">{e.name}</span>
                  {e.ref_title && (
                    <span className="text-[color:var(--nq-stone)]">re: {e.ref_title}</span>
                  )}
                  <span className="ml-auto text-xs text-[color:var(--nq-stone)]">
                    {formatDateTime(e.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { to: "/admin/pages", label: "Edit pages", icon: FileText },
          { to: "/admin/catalogue", label: "Edit catalogue", icon: Boxes },
          { to: "/admin/media", label: "Media library", icon: Image },
          { to: "/admin/settings", label: "Site settings", icon: Star },
        ].map((q) => (
          <Link key={q.to} to={q.to} className="card-nq flex items-center gap-3 p-4">
            <q.icon className="h-4 w-4 gold-text" />
            <span className="text-sm font-medium">{q.label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
